import {
  faArrowRightFromBracket,
  faChevronDown,
  faPenToSquare,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import gpLogo from "assets/images/Grace-Papers-Logo_Blue_small.png";
import { useWindowDimensions } from "hooks/windowDimensions";
import { useEffect, useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import styled from "styled-components/macro";
import { useStoreContext } from "utils/GlobalState";
import { logout as apiLogout } from "../../helpers/api";

const Header = styled.header`
  background-color: ${(props) => props.theme.colors.primaryBackground};
  border-bottom: 1px solid ${(props) => props.theme.colors.lightGrey};
  position: sticky;
  top: 0;
  z-index: 100;
`;

const Container = styled.div`
  margin: 0 auto;
  max-width: 100rem;
  padding: 0 1.5rem;
  height: 3.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const NavSection = styled.nav`
  display: flex;
  align-items: center;
  gap: 2rem;
`;

const Brand = styled(NavLink)`
  font-size: ${(props) => props.theme.fontSize.md};
  font-weight: bold;
  text-decoration: none;
  display: flex;
  align-items: center;

  .brand-primary {
    color: ${(props) => props.theme.colors.heroBlue};
  }

  .brand-secondary {
    color: ${(props) => props.theme.colors.heading};
  }
`;

const NavList = styled.ul`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  list-style: none;
  margin: 0;
  padding: 0;
`;

const StyledNavLink = styled(NavLink).attrs({
  activeClassName: "active",
})`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  text-decoration: none;
  font-size: ${(props) => props.theme.fontSize.xs};
  font-weight: 500;
  color: ${(props) => props.theme.colors.body};
  padding-bottom: 2px;
  border-bottom: 2px solid transparent;
  transition: color 280ms ease-out;

  &:hover {
    color: ${(props) => props.theme.colors.heading};
  }

  &.active {
    color: ${(props) => props.theme.colors.heroBlue};
    // border-bottom-color: ${(props) => props.theme.colors.heroBlue};
  }
`;

const DropdownItem = styled.li`
  position: relative;
  top: -2px;
  list-style: none;

  .dropdown__trigger {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    font-size: ${(props) => props.theme.fontSize.xs};
    font-weight: 500;
    color: ${(props) => props.theme.colors.body};
    cursor: pointer;
    background: transparent;
    border: none;
    font-family: ${(props) => props.theme.fontFamily.main};
    padding: 0;
    transition: color 280ms ease-out;

    &:hover {
      color: ${(props) => props.theme.colors.heading};
    }

    &:focus {
      outline: none;
    }
  }

  .dropdown__menu {
    background-color: ${(props) => props.theme.colors.primaryBackground};
    position: absolute;
    min-width: 18ch;
    left: 0;
    top: calc(100% + 0.7rem);
    transition: 280ms all 120ms ease-out;
    transform: rotateX(-90deg);
    transform-origin: top center;
    visibility: hidden;
    opacity: 0;
    padding: 0.4rem 0;
    border-radius: ${(props) => props.theme.borderRadius.md};
    box-shadow: ${(props) => props.theme.boxShadow.md};
    z-index: 100;
    list-style: none;
    margin: 0;

    a,
    button {
      display: block;
      padding: 0.5rem 1rem;
      opacity: 0;
      transition: 280ms all 120ms ease-out;
      text-decoration: none;
      font-size: ${(props) => props.theme.fontSize.xs};
      color: ${(props) => props.theme.colors.heading};
      background: transparent;
      border: none;
      cursor: pointer;
      width: 100%;
      text-align: left;
      font-family: ${(props) => props.theme.fontFamily.main};

      &:hover {
        background-color: ${(props) => props.theme.colors.altBlue};
      }

      &:focus {
        outline: none;
        background-color: ${(props) => props.theme.colors.altBlue};
      }
    }
  }

  &:hover,
  &:focus-within {
    .dropdown__menu {
      opacity: 1;
      transform: rotateX(0);
      visibility: visible;

      a,
      button {
        opacity: 1;
      }
    }
  }
`;

const ActionsSection = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const Avatar = styled.button`
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  background-color: ${(props) => props.theme.colors.heroBlue};
  color: ${(props) => props.theme.colors.primaryBackground};
  font-size: 14px;
  font-weight: 600;
  font-family: ${(props) => props.theme.fontFamily.main};
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid ${(props) => props.theme.colors.primaryBackground};
  box-shadow: 0 0 0 1px ${(props) => props.theme.colors.heroBlue};
  cursor: pointer;
  flex-shrink: 0;
  transition: box-shadow 180ms ease-out, opacity 180ms ease-out;

  &:hover {
    opacity: 0.9;
    box-shadow: 0 0 0 2px ${(props) => props.theme.colors.heroBlue};
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px ${(props) => props.theme.colors.heroBlue};
  }
`;

const AvatarWrapper = styled.div`
  position: relative;
`;

const UserPopover = styled.div`
  position: absolute;
  right: 0;
  top: calc(100% + 0.5rem);
  width: 18rem;
  background-color: ${(props) => props.theme.colors.primaryBackground};
  border-radius: ${(props) => props.theme.borderRadius.lg};
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.14), 0 1px 4px rgba(0, 0, 0, 0.08);
  z-index: 200;
  overflow: hidden;
  pointer-events: ${(props) => (props.$open ? "all" : "none")};
  opacity: ${(props) => (props.$open ? 1 : 0)};
  transform: ${(props) =>
    props.$open ? "translateY(0) scale(1)" : "translateY(-6px) scale(0.97)"};
  transform-origin: top right;
  transition: opacity 180ms ease-out, transform 180ms ease-out;
  border: 1px solid ${(props) => props.theme.colors.lightGrey};
`;

const PopoverUserSection = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1rem 0.875rem;
  background: linear-gradient(
    135deg,
    ${(props) => props.theme.colors.altBlue} 0%,
    ${(props) => props.theme.colors.primaryBackground} 100%
  );
`;

const PopoverAvatarImage = styled.img`
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  border: 2px solid ${(props) => props.theme.colors.primaryBackground};
  box-shadow: 0 0 0 2px ${(props) => props.theme.colors.heroBlue};
`;

const PopoverAvatarPlaceholder = styled.div`
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 50%;
  background-color: ${(props) => props.theme.colors.heroBlue};
  color: ${(props) => props.theme.colors.primaryBackground};
  font-size: ${(props) => props.theme.fontSize.sm};
  font-weight: 700;
  font-family: ${(props) => props.theme.fontFamily.main};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border: 2px solid ${(props) => props.theme.colors.primaryBackground};
  box-shadow: 0 0 0 2px ${(props) => props.theme.colors.heroBlue};
`;

const PopoverUserInfo = styled.div`
  overflow: hidden;
`;

const PopoverUserName = styled.p`
  margin: 0;
  font-size: ${(props) => props.theme.fontSize.sm};
  font-weight: 700;
  color: ${(props) => props.theme.colors.heading};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PopoverUserEmail = styled.p`
  margin: 0.1rem 0 0;
  font-size: ${(props) => props.theme.fontSize.xxs};
  color: ${(props) => props.theme.colors.body};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PopoverUserCompany = styled.p`
  margin: 0.3rem 0 0;
  font-size: ${(props) => props.theme.fontSize.xs};
  font-weight: 600;
  color: ${(props) => props.theme.colors.body};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  letter-spacing: 0.02em;
`;

const PopoverDivider = styled.hr`
  margin: 0;
  border: none;
  border-top: 1px solid ${(props) => props.theme.colors.lightGrey};
`;

const PopoverActions = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0.375rem 0;
`;

const PopoverActionItem = styled.li``;

const PopoverActionLink = styled(NavLink)`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.625rem 1rem;
  text-decoration: none;
  font-size: ${(props) => props.theme.fontSize.xs};
  font-weight: 500;
  color: ${(props) => props.theme.colors.heading};
  transition: background-color 150ms ease-out, color 150ms ease-out;

  .action-icon {
    width: 1rem;
    text-align: center;
    color: ${(props) => props.theme.colors.body};
    transition: color 150ms ease-out;
  }

  &:hover {
    background-color: ${(props) => props.theme.colors.altBlue};
    color: ${(props) => props.theme.colors.heroBlue};

    .action-icon {
      color: ${(props) => props.theme.colors.heroBlue};
    }
  }
`;

const PopoverActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.625rem 1rem;
  width: 100%;
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: ${(props) => props.theme.fontSize.xs};
  font-weight: 500;
  font-family: ${(props) => props.theme.fontFamily.main};
  color: ${(props) => props.theme.colors.errorRed};
  transition: background-color 150ms ease-out;
  text-align: left;

  .action-icon {
    width: 1rem;
    text-align: center;
    color: ${(props) => props.theme.colors.errorRed};
  }

  &:hover {
    background-color: hsl(0, 100%, 97%);
  }

  &:focus {
    outline: none;
    background-color: hsl(0, 100%, 97%);
  }
`;

const primaryNavItems = [
  { label: "My Program", to: "/program" },
  { label: "Explore", to: "/explore" },
  { label: "EmpowerMe", to: "/empower-me" },
  { label: "Professional Vision", to: "/my-insights" },
  { label: "Events", to: "/events" },
];

const moreNavItems = [
  { label: "My Workplace", to: "/company-policy" },
  /* { label: "Account", to: "/account" }, */
  { label: "Terms", to: "/terms-and-conditions" },
  { label: "Privacy", to: "/privacy-statement" },
];

export default function NewNavbar() {
  const { width } = useWindowDimensions();
  const { state, dispatch } = useStoreContext();
  const { userObject } = state;
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const popoverRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        setIsPopoverOpen(false);
      }
    }
    if (isPopoverOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isPopoverOpen]);

  const initials =
    userObject?.first_name && userObject?.last_name
      ? `${userObject.first_name[0]}${userObject.last_name[0]}`
      : "GP";

  const fullName = userObject?.first_name
    ? `${userObject.first_name} ${userObject.last_name ?? ""}`.trim()
    : "Grace Papers";

  const logout = async (e) => {
    e.preventDefault();
    setIsPopoverOpen(false);
    await apiLogout(dispatch);
  };

  return (
    <Header>
      <Container>
        <NavSection>
          <Brand to="/" exact activeClassName="">
            <img src={gpLogo} alt="Grace Papers" style={{ height: 24 }} />
          </Brand>

          {width > 768 ? (
            <NavList>
              {primaryNavItems.map((item) => (
                <li key={item.to}>
                  <StyledNavLink to={item.to} activeClassName="active">
                    {item.label}
                  </StyledNavLink>
                </li>
              ))}

              <DropdownItem>
                <button className="dropdown__trigger">
                  More{" "}
                  <FontAwesomeIcon
                    icon={faChevronDown}
                    style={{ fontSize: "0.7rem" }}
                  />
                </button>
                <ul className="dropdown__menu">
                  {moreNavItems.map((item) => (
                    <li key={item.to}>
                      <StyledNavLink to={item.to} activeClassName="active">
                        {item.label}
                      </StyledNavLink>
                    </li>
                  ))}
                  {/* <li>
                    <button onClick={logout}>Logout</button>
                  </li> */}
                </ul>
              </DropdownItem>
            </NavList>
          ) : null}
        </NavSection>

        <ActionsSection>
          {width <= 768 && (
            <DropdownItem>
              <button className="dropdown__trigger">
                Pages{" "}
                <FontAwesomeIcon
                  icon={faChevronDown}
                  style={{ fontSize: "0.7rem" }}
                />
              </button>
              <ul className="dropdown__menu" style={{ right: "auto", left: 0 }}>
                {[...primaryNavItems, ...moreNavItems].map((item) => (
                  <li key={item.to}>
                    <StyledNavLink to={item.to} activeClassName="active">
                      {item.label}
                    </StyledNavLink>
                  </li>
                ))}
                <li>
                  <button onClick={logout}>Logout</button>
                </li>
              </ul>
            </DropdownItem>
          )}

          {/* <IconButton aria-label="Notifications">
            <FontAwesomeIcon icon={faBell} />
          </IconButton> */}

          <AvatarWrapper ref={popoverRef}>
            <Avatar
              aria-label="User menu"
              aria-expanded={isPopoverOpen}
              onClick={() => setIsPopoverOpen((prev) => !prev)}
            >
              {userObject?.image ? (
                <img
                  src={userObject.image}
                  alt={fullName}
                  style={{
                    width: "100%",
                    height: "100%",
                    borderRadius: "50%",
                    objectFit: "cover",
                  }}
                />
              ) : (
                initials
              )}
            </Avatar>

            <UserPopover $open={isPopoverOpen}>
              <PopoverUserSection>
                {userObject?.image ? (
                  <PopoverAvatarImage src={userObject.image} alt={fullName} />
                ) : (
                  <PopoverAvatarPlaceholder>
                    {initials}
                  </PopoverAvatarPlaceholder>
                )}
                <PopoverUserInfo>
                  <PopoverUserName>{fullName}</PopoverUserName>
                  <PopoverUserEmail>{userObject?.email ?? ""}</PopoverUserEmail>
                  {userObject?.company?.name && (
                    <PopoverUserCompany>
                      {userObject.company.name}
                    </PopoverUserCompany>
                  )}
                </PopoverUserInfo>
              </PopoverUserSection>

              <PopoverDivider />

              <PopoverActions>
                <PopoverActionItem>
                  <PopoverActionLink
                    to="/profile"
                    onClick={() => setIsPopoverOpen(false)}
                  >
                    <span className="action-icon">
                      <FontAwesomeIcon icon={faPenToSquare} />
                    </span>
                    Edit Profile
                  </PopoverActionLink>
                </PopoverActionItem>

                <PopoverActionItem>
                  <PopoverActionButton onClick={logout}>
                    <span className="action-icon">
                      <FontAwesomeIcon icon={faArrowRightFromBracket} />
                    </span>
                    Logout
                  </PopoverActionButton>
                </PopoverActionItem>
              </PopoverActions>
            </UserPopover>
          </AvatarWrapper>
        </ActionsSection>
      </Container>
    </Header>
  );
}
